/**
 * Module responsible for extracting the necessary data for Analytics before calling Rudderstack client
 * - There are some cases where the data is on the DOM as data attributes ie: data-analytics-<section_name>-<attribute_identificator>
 *
 */

import { TRACK_CART_SOURCE, TRACK_COLLECTION_FILTER_TYPE } from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import { gtmProductAdded, gtmProductRemoved, gtmProductViewed } from '~/lib/gtm';
import * as RudderstackClient from '~/lib/rudderstack/rudderstack-client';
import * as RudderstackUtils from '~/lib/rudderstack/rudderstack-utils';
import * as UrlUtils from '~/utils/url';

/**
 * Get the navigation source as current User page URL
 * ie: https://www.allbirds.com/products/mens-wool-crew-socks
 *
 * @returns {string} The navigation source
 */
const _getNavigationSource = () => `${window.location.hostname}${window.location.pathname}`;

/**
 * Warn on a log when the props object has missing property, probably due to missed data-analytics attribute
 *
 * @param {Object} param
 * @property {Object} propsObject - The object with the data attributes
 * @property {string} sectionName - The name of the section to hook the analytics events
 */
const _warnMissedDataAtrtibutesProps = ({ propsObject, sectionName = 'not_provided' }) => {
  Object.entries(propsObject).forEach(([key, value]) => {
    if (!value) {
      DataDogLogs.warn(
        `[Allbirds analytics] - Data Extraction - warnMissedDataAtrtibutesProps - ${key} is missed`,
        { sectionName },
      );
    }
  });
};

/**
 * Get the section container from the DOM
 *
 * @param {string} querySelector - The query selector to use to get the section container
 *
 * @returns {Element} The section container
 */
const _getMainSectionContainer = (querySelector) => {
  const sectionContainer = document.querySelector(querySelector);
  if (!sectionContainer) {
    throw new Error(`Section container not found for query selector: ${querySelector}`);
  }

  return sectionContainer;
};

/**
 * Function used on Homepage sections to append Rudderstack Query String API event 'Homepage Module Clicked'
 *  to all the anchor elements in the section that have the data attributes
 *
 * This function will add the query string to the CTA links in the section or leave the original rendered by SSR liquid.
 *
 * Use this funciton on onMounted lifecycle hook of the Vue app.
 *
 * How to use this method:
 *  1. Add the data attributes to the section or blocks markup where the CTAs are rendered
 *  2. The data attributes should follow the naming convention 'data-analytics-<section_name>-<attribute_identificator>'
 *     ie: This is an example of data attributes for the section 'color-grid':
 *
 *       data-analytics-color-grid-section-container
 *       data-analytics-color-grid-section-name
 *       data-analytics-color-grid-module-position
 *       data-analytics-color-grid-anchor-element
 *       data-analytics-color-grid-image-name
 *       data-analytics-color-grid-cta-name
 *       data-analytics-color-grid-cta-url
 *       data-analytics-color-grid-card-name
 *       data-analytics-color-grid-card-position
 *
 * @param {Object} param
 * @property {string} sectionName - The name of the section to hook the analytics events
 */
const hookHomepageModuleClicked = ({ sectionName }) => {
  if (!sectionName) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookHomepageModuleClickedAnalyticsEvents - sectionName is required',
      {},
      new Error('sectionName is required'),
    );

    return;
  }

  try {
    // Get section related data
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );
    const analyticModuleName = sectionContainer.getAttribute(
      `data-analytics-${sectionName}-section-name`,
    );
    const analyticModulePosition = sectionContainer.getAttribute(
      `data-analytics-${sectionName}-module-position`,
    );

    // Get all the CTAs in the section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticImageName = ctaLink.getAttribute(`data-analytics-${sectionName}-image-name`);
      const analyticCtaName = ctaLink.getAttribute(`data-analytics-${sectionName}-cta-name`);
      const analyticCtaDestination = ctaLink.getAttribute(`data-analytics-${sectionName}-cta-url`);
      const analyticCardName = ctaLink.getAttribute(`data-analytics-${sectionName}-card-name`);
      const analyticCardPosition = ctaLink.getAttribute(
        `data-analytics-${sectionName}-card-position`,
      );

      const baseUrl = ctaLink.href;

      // Warn missed data attributes
      _warnMissedDataAtrtibutesProps({
        propsObject: {
          analyticCardName,
          analyticCardPosition,
          analyticCtaDestination,
          analyticCtaName,
          analyticImageName,
          analyticModuleName,
          analyticModulePosition,
          baseUrl,
        },
        sectionName,
      });

      // Build the URL with the query string
      const ctaUrlWithAnalytics = RudderstackClient.homepageModuleClickQueryStringUrlBuilder({
        baseUrl,
        carouselCardName: analyticCardName,
        carouselCardPosition: analyticCardPosition,
        imageName: analyticImageName,
        linkCTAName: analyticCtaName,
        linkDestination: analyticCtaDestination,
        moduleName: analyticModuleName,
        modulePosition: analyticModulePosition,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookHomepageModuleClickedAnalitycsEvents - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Function used on Homepage hero section to append Rudderstack Query String API event 'Homepage Hero Clicked'
 *  to all the anchor elements in the section that have the data attributes
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-image-name
 * - data-analytics-<section_name>-cta-name
 * - data-analytics-<section_name>-cta-url
 *
 * @param {Object} param
 * @property {string} sectionName - The name of the section to hook the analytics events
 */
const hookHomepageHeroClicked = ({ sectionName }) => {
  try {
    if (!sectionName) {
      DataDogLogs.error(
        '[Allbirds analytics] - Data Extraction - hookHomepageHeroClicked - sectionName is required',
        {},
        new Error('sectionName is required'),
      );

      return;
    }

    // Get main section container
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );

    // Get all the CTAs in the hero section
    const homepageHeroCTAsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(homepageHeroCTAsList).forEach((ctaLink) => {
      const analyticsImageName = ctaLink.getAttribute(`data-analytics-${sectionName}-image-name`);
      const analyticCtaName = ctaLink.getAttribute(`data-analytics-${sectionName}-cta-name`);
      const analyticCtaLink = ctaLink.getAttribute(`data-analytics-${sectionName}-cta-url`);

      const baseUrl = ctaLink.href;

      // Warn missed data attributes
      _warnMissedDataAtrtibutesProps({
        propsObject: {
          analyticCtaLink,
          analyticCtaName,
          analyticsImageName,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.homepageHeroClickQueryStringUrlBuilder({
        baseUrl,
        heroImageName: analyticsImageName,
        linkCTAName: analyticCtaName,
        linkDestination: analyticCtaLink,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookHomepageHeroClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Function used to trigger the Rudderstack analytics event 'Navigation Clicked'
 *
 * @param {Object} param
 * @property {string} navigationLevel1
 * @property {string} navigationLevel2
 * @property {string} navigationLevel3
 * @property {string} navigationType
 */
const triggerNavigationViewed = ({
  navigationLevel1,
  navigationLevel2,
  navigationLevel3,
  navigationType,
}) => {
  try {
    RudderstackClient.navigationViewed({
      navigationLevel1,
      navigationLevel2,
      navigationLevel3,
      navigationSource: _getNavigationSource(),
      navigationType,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerNavigationViewed - Error while calling the analytics events',
      {},
      error,
    );
  }
};

/**
 * Function used on any section to append Rudderstack Query String API event 'Navigation Clicked'
 *  to all the anchor elements in the section that have the data attributes
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-navigation-level-1
 * - data-analytics-<section_name>-navigation-level-2
 * - data-analytics-<section_name>-navigation-level-3
 * - data-analytics-<section_name>-navigation-type
 *
 * @param {Object} param
 * @property {string} sectionName - The name of the section to hook the analytics events
 */
const hookNavigationClicked = ({ sectionName }) => {
  try {
    if (!sectionName) {
      DataDogLogs.error(
        '[Allbirds analytics] - Data Extraction - hookNavigationClicked - sectionName is required',
        {},
        new Error('sectionName is required'),
      );

      return;
    }

    // Get main section container
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );

    // Get all the CTAs in the hero section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticsNavigationLevel1 = ctaLink.getAttribute(
        `data-analytics-${sectionName}-navigation-level-1`,
      );
      const analyticsNavigationLevel2 = ctaLink.getAttribute(
        `data-analytics-${sectionName}-navigation-level-2`,
      );
      const analyticsNavigationLevel3 = ctaLink.getAttribute(
        `data-analytics-${sectionName}-navigation-level-3`,
      );

      const analyticsNavigationType = ctaLink.getAttribute(
        `data-analytics-${sectionName}-navigation-type`,
      );

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes
      _warnMissedDataAtrtibutesProps({
        propsObject: {
          analyticsNavigationType,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.navigationClickedQueryStringUrlBuilder({
        baseUrl,
        navigationLevel1: analyticsNavigationLevel1,
        navigationLevel2: analyticsNavigationLevel2,
        navigationLevel3: analyticsNavigationLevel3,
        navigationSource: _getNavigationSource(),
        navigationType: analyticsNavigationType,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookNavigationClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Collect product data and size selected data and trigger the product viewed event
 * If the variant size is provided, use it, otherwise attempt to get it from the query string
 * If the variant size contains a dot, replace it with a dash to standardize the size
 *
 * @param {Object} param
 * @property {string} variantSize - The size of the variant to be used in the product viewed event
 */
const triggerProductViewed = ({ variantSize = null } = {}) => {
  try {
    const product = window.ALLBIRDS?.page?.productDetailsPage?.product;
    let sizeSelected = variantSize;

    // If the variant size is not provided, attempt to get it from the query string
    if (!variantSize) {
      const currentQueryString = UrlUtils.parseQueryString();
      sizeSelected = currentQueryString['size'];
    }

    // If size was selected attempt to replace dots with dash to standardize the size
    if (sizeSelected) {
      sizeSelected = RudderstackUtils.getNormalizedSize(sizeSelected);
    }

    RudderstackClient.productViewed({
      product,
      sizeSelected,
    });
    gtmProductViewed(product, sizeSelected);
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductViewed - Error while triggering the product viewed event',
      {},
      error,
    );
  }
};

/**
 * Collect collection data and trigger the product list viewed event
 */
const triggerProductListViewed = () => {
  try {
    const collection = window.ALLBIRDS?.page?.collectionPage?.collection;

    RudderstackClient.productListViewed({
      collection,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductListViewed - Error while triggering the product list viewed event',
      {},
      error,
    );
  }
};

/**
 * Collect collection data with filters applied and trigger the product list filtered event if any filters are applied
 */
const triggerProductListFiltered = () => {
  try {
    const collection = window.ALLBIRDS?.page?.collectionPage?.collection;
    const currentQueryString = UrlUtils.parseQueryString();
    const clearFilters = currentQueryString['clear_filters'];

    // If the collection has filters applied or clear filters is true
    if (collection && (collection.filters_applied.length > 0 || clearFilters)) {
      RudderstackClient.productListFiltered({
        collection,
      });
    }
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductListFiltered - Error while triggering the product list filtered event',
      {},
      error,
    );
  }
};

/**
 * Get the feature type from the data attribute
 *
 * @param {string} featureType - The feature type from the data attribute
 *
 * @returns {string} The feature type
 */
const getFeatureType = (featureType) => {
  let result = 'feature_type_not_found';

  if (featureType === TRACK_COLLECTION_FILTER_TYPE.PILL_TOGGLE.KEY) {
    result = TRACK_COLLECTION_FILTER_TYPE.PILL_TOGGLE.RUDDERSTACK_NAME;
  } else if (featureType === TRACK_COLLECTION_FILTER_TYPE.SORTING.KEY) {
    result = TRACK_COLLECTION_FILTER_TYPE.SORTING.RUDDERSTACK_NAME;
  }

  return result;
};

/**
 * Function used on any section to append Rudderstack Query String API event 'Product List Clicked'
 *  to all the anchor elements in the section that have the data attributes
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-product-id
 * - data-analytics-<section_name>-card-position
 *
 * @param {Object} param
 * @property {string} sectionName - The name of the section to hook the analytics events
 * @property {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 */
const hookProductListClicked = ({ collection, sectionName }) => {
  if (!collection || !sectionName) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookProductListClicked - collection and sectionName are required',
      { collection, sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  try {
    // Get main section container
    const sectionContainer = _getMainSectionContainer(`[data-analytics-${sectionName}]`);

    // Get all the CTAs in the hero section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticsProductClickedId = ctaLink.getAttribute(
        `data-analytics-${sectionName}-product-id`,
      );
      const analyticsProductClickedPosition = ctaLink.getAttribute(
        `data-analytics-${sectionName}-card-position`,
      );

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes
      _warnMissedDataAtrtibutesProps({
        propsObject: {
          analyticsProductClickedId,
          analyticsProductClickedPosition,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.productListClickedQueryStringUrlBuilder({
        baseUrl,
        collection,
        productClickedId: analyticsProductClickedId,
        productClickedPosition: analyticsProductClickedPosition,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookProductListClicked - Error while hooking the analytics events',
      { collection, sectionName },
      error,
    );
  }
};

/**
 * Function used on any section to append 'Query String API' for 'Product List Feature Clicked'
 *
 * When 'useDomData' is true, the anchor elements should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-cta-name
 * - data-analytics-<section_name>-cta-url
 *
 * When 'useDomData' is false, 'featureName' and 'featureUrlObject' are required as params on this function
 *
 * @param {Object} param - Object with several params
 * @property {string} sectionName - The name of the section to hook the analytics events
 * @property {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 * @property {URL} featureUrlObject - The URL object to be updated with the query params
 * @property {string} featureName - The name of the feature ie: 'WOMEN'
 * @property {string} featureType - The type of the feature ie: 'pill toggle', 'sorting'
 * @property {boolean} useDomData - Whether to use the DOM data or the URL object and params
 */
const hookProductListFeatureClicked = ({
  collection = null,
  featureName = null,
  featureType = null,
  featureUrlObject = null,
  sectionName = null,
  useDomData = true,
}) => {
  // Validate required params for all cases
  if (!collection || !sectionName || !featureType) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookProductListFeatureClicked - collection and sectionName are required',
      { collection, sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  // Validate required params if used useDomData is false
  if (!useDomData) {
    if (!featureUrlObject || !featureUrlObject.searchParams || !featureName) {
      DataDogLogs.error(
        '[Allbirds analytics] - Data Extraction - hookProductListFeatureClicked - collection and sectionName are required',
        { collection, sectionName },
        new Error('Missing required params for urlObject and featureObj'),
      );

      return;
    }
  }

  try {
    // If the URL object is provided, update the URL with the query params, otherwise, decorate the href on the DOM
    if (useDomData) {
      // Get main section container
      const sectionContainer = _getMainSectionContainer(`[data-analytics-${sectionName}]`);

      // Get all the CTAs in the hero section
      const anchorElementsList = sectionContainer.querySelectorAll(
        `[data-analytics-feature-clicked-${sectionName}-anchor-element]`,
      );

      // For each CTA, get the data attributes and build the URL with the query string
      Array.from(anchorElementsList).forEach((ctaLink) => {
        const analyticsCtaName = ctaLink.getAttribute(
          `data-analytics-feature-clicked-${sectionName}-cta-name`,
        );
        const analyticsCtaUrl = ctaLink.getAttribute(
          `data-analytics-feature-clicked-${sectionName}-cta-url`,
        );

        const baseUrl = ctaLink.href;

        // Warn mandatory missed data attributes
        _warnMissedDataAtrtibutesProps({
          propsObject: {
            analyticsCtaName,
            analyticsCtaUrl,
            baseUrl,
          },
          sectionName,
        });

        const ctaUrlWithAnalytics =
          RudderstackClient.productListFeatureClickedQueryStringUrlBuilder({
            baseUrl,
            collection,
            featureCTA: analyticsCtaName,
            featureLinkDestination: analyticsCtaUrl,
            featureType: getFeatureType(featureType),
          });

        // Only set the href if it is not empty otherwise leave the original href
        if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
      });
    } else {
      // Update provided urlObject with the feature name, type and collection data
      const linkDestinationProcessed = `${featureUrlObject.pathname}${featureUrlObject.search}`;

      const ctaUrlWithAnalytics = RudderstackClient.productListFeatureClickedQueryStringUrlBuilder({
        baseUrl: featureUrlObject.toString(),
        collection,
        featureCTA: featureName,
        featureLinkDestination: linkDestinationProcessed,
        featureType: getFeatureType(featureType),
      });

      // Only update the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) featureUrlObject.href = ctaUrlWithAnalytics;
    }
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookProductListFeatureClicked - Error while hooking the analytics events',
      { collection, sectionName },
      error,
    );
  }
};

/**
 * Function used on any section to append 'Query String API' for 'Global Banner Link Clicked'
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-global-banner-name
 *
 * @param {Object} param with several properties
 * @property {string} sectionName - The name of the section to hook the analytics events
 *
 * @returns {void}
 */
const hookGlobalBannerLinkClicked = ({ sectionName }) => {
  if (!sectionName) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookGlobalBannerLinkClicked - sectionName is required',
      { sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  try {
    // Get main section container
    const sectionContainer = _getMainSectionContainer(`[data-analytics-${sectionName}]`);

    // Get all the CTAs in the hero section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticsGlobalBannerName = ctaLink.getAttribute(
        `data-analytics-${sectionName}-global-banner-name`,
      );

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes
      _warnMissedDataAtrtibutesProps({
        propsObject: {
          analyticsGlobalBannerName,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.globalBannerClickedQueryStringUrlBuilder({
        baseUrl,
        globalBannerName: analyticsGlobalBannerName,
        globalHeaderSource: window.location.href,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookGlobalBannerLinkClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Function used on any section to append 'Query String API' for event 'Footer Link Clicked'
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-link-category
 * - data-analytics-<section_name>-link-name
 *
 * @param {Object} param with several properties
 * @property {string} sectionName - The name of the section to hook the analytics events
 *
 * @returns {void}
 */
const hookFooterLinkClicked = ({ sectionName }) => {
  if (!sectionName) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookFooterLinkClicked - sectionName is required',
      { sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  try {
    // Get main section container
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );

    // Get all the CTAs in the hero section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticsFooterLinkCategory = ctaLink.getAttribute(
        `data-analytics-${sectionName}-link-category`,
      );
      const analyticsFooterLinkName = ctaLink.getAttribute(
        `data-analytics-${sectionName}-link-name`,
      );

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes
      _warnMissedDataAtrtibutesProps({
        propsObject: {
          analyticsFooterLinkCategory,
          analyticsFooterLinkName,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.footerLinkClickedQueryStringUrlBuilder({
        baseUrl,
        footerLinkCategory: analyticsFooterLinkCategory,
        footerLinkName: analyticsFooterLinkName,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookFooterLinkClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 *  Function used to send event 'Add to Cart'
 *
 * @param {Object} cart the full cart object
 * @param {Object} productToUpdate the product to be added to the cart
 * @param {string} source the source of the event
 * @param {string} sizeSelected - The size of the variant
 */
const triggerProductAdded = (cart, productToUpdate, source, sizeSelected) => {
  RudderstackClient.productAdded({
    ajaxCartItems: cart?.items,
    productAddedAjaxCart:
      source === TRACK_CART_SOURCE.CART || source === TRACK_CART_SOURCE.UPSELL
        ? productToUpdate
        : null,
    productAddedShopify: source === TRACK_CART_SOURCE.PDP ? productToUpdate : null,
    productAddedSource: source,
    productSizeSelected: sizeSelected || productToUpdate?.variant_title,
  });
  gtmProductAdded(productToUpdate, cart, source);
};

/**
 *  Function used to send event 'Remove from Cart'
 *
 * @param {Object} cart the full cart object
 * @param {Object} productRemoved the product to be added to the cart
 * @param {number} currentQuantity the quantity of product to remove
 */
const triggerProductRemoved = (cart, productRemoved, currentQuantity) => {
  RudderstackClient.productRemoved({
    ajaxCartItems: cart?.items,
    productRemovedAjaxCart: productRemoved,
    productRemovedQuantity: currentQuantity,
  });
  gtmProductRemoved(productRemoved, cart);
};

export {
  hookFooterLinkClicked,
  hookGlobalBannerLinkClicked,
  hookHomepageHeroClicked,
  hookHomepageModuleClicked,
  hookNavigationClicked,
  hookProductListClicked,
  hookProductListFeatureClicked,
  triggerNavigationViewed,
  triggerProductAdded,
  triggerProductListFiltered,
  triggerProductListViewed,
  triggerProductRemoved,
  triggerProductViewed,
};
